import { useState, type ReactElement } from 'react';
import type { RuntimeOutage } from '../lib/data';
import { currentOutages } from '../lib/outages';
import { latestCheckAgainInstruction, receiptForCheckAgain, type ControlReceiptView } from '../lib/control-receipt';
import { canCheckAgain, recheckRefusalReason, type RecheckInstructionState, type RecheckResult } from '../lib/runtime-recheck';
import { ControlReceiptNote } from './ControlReceiptNote';

interface CheckAgainPress {
  readonly baselineIssuedAt: string | null;
  readonly alreadyWaiting: boolean;
  readonly submitRefusal: string | null;
}

/**
 * Which tools a computer cannot use at the moment, and when each comes back.
 * Work is handed to another tool without anyone being told, so this is the one
 * place the reason is said out loud. Nothing is drawn while every tool is
 * usable - an empty heading would only be noise on a healthy screen.
 *
 * #672: a tool parked out of usage offers "Check again" when the room provides it. That only
 * asks the computer to try the tool once; the line changes when Brunel's report does.
 */
export function OutagesPanel({ outages, nowMs, onCheckAgain, recheckInstructions = [] }: {
  outages: readonly RuntimeOutage[];
  nowMs: number;
  onCheckAgain?: (row: RuntimeOutage, onResult: (result: RecheckResult) => void) => Promise<void>;
  /** What each computer already said about Check again. Display only. */
  recheckInstructions?: readonly RecheckInstructionState[];
}): ReactElement | null {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<RecheckResult | null>(null);
  const [lineResult, setLineResult] = useState<Readonly<Record<string, RecheckResult>>>({});
  const [presses, setPresses] = useState<Readonly<Record<string, CheckAgainPress>>>({});
  const lines = currentOutages(outages, nowMs);
  if (lines.length === 0) return null;
  const rowFor = new Map<string, RuntimeOutage>(outages.map((row) => [`${row.node_id}::${row.runtime_key}`, row]));
  const checkAgain = (key: string, row: RuntimeOutage): void => {
    if (!onCheckAgain || busyKey !== null) return;
    const prior = latestCheckAgainInstruction(recheckInstructions, row.node_id, row.runtime_key);
    setPresses((current) => ({
      ...current,
      [key]: { baselineIssuedAt: prior?.issued_at ?? null, alreadyWaiting: false, submitRefusal: null },
    }));
    setBusyKey(key);
    setNotice(null);
    void onCheckAgain(row, (result) => {
      setLineResult((current) => ({ ...current, [key]: result }));
      setNotice(result.message.trim() === '' ? null : result);
      setPresses((current) => ({
        ...current,
        [key]: {
          baselineIssuedAt: current[key]?.baselineIssuedAt ?? prior?.issued_at ?? null,
          alreadyWaiting: result.ok === true && result.alreadyWaiting === true,
          submitRefusal: result.ok ? null : result.message,
        },
      }));
    }).finally(() => setBusyKey(null));
  };
  return (
    <div className="stack">
      <h2>Tools not in use</h2>
      <ul className="list">
        {lines.map((line) => {
          const row = rowFor.get(line.key);
          const checkable = onCheckAgain !== undefined && row !== undefined && canCheckAgain(row, nowMs);
          const standing = row === undefined ? null : recheckRefusalReason(recheckInstructions, row.node_id, row.runtime_key);
          const clicked = lineResult[line.key];
          const refusal = standing ?? (clicked !== undefined && !clicked.ok && clicked.message.trim() !== '' ? clicked.message : null);
          const press = presses[line.key];
          const receipt: ControlReceiptView | null = press === undefined || row === undefined ? null : receiptForCheckAgain({
            baselineIssuedAt: press.baselineIssuedAt,
            alreadyWaiting: press.alreadyWaiting,
            submitRefusal: press.submitRefusal,
            rows: recheckInstructions,
            nodeId: row.node_id,
            runtimeKey: row.runtime_key,
          });
          return (
            <li key={line.key}>
              <div className={`statuspanel tone-${line.tone}`}>
                <p className="status-line">
                  <span className={`dot dot-${line.tone}`} aria-hidden="true" />
                  {line.toolName}
                  <span className="muted"> · {line.nodeName}</span>
                </p>
                <span>{line.text}</span>
                {checkable && (
                  <div className="settings-recheck">
                    <button type="button" className="link" disabled={busyKey !== null}
                      title="Run this tool once on that computer, reply-only. The report clears only if it answers."
                      aria-label={`Check ${line.toolName} on ${line.nodeName} again`}
                      onClick={() => checkAgain(line.key, row)}>
                      {busyKey === line.key ? 'Asking…' : 'Check again'}
                    </button>
                    {receipt !== null && <ControlReceiptNote view={receipt} action="Check again" />}
                    {refusal !== null && <span className="error settings-recheck-reason" role="status">{refusal}</span>}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {notice && <p className={notice.ok ? 'muted' : 'error'} role="status">{notice.message}</p>}
      <small className="muted">Work is given to another tool while one is out, so a run still finishes.</small>
    </div>
  );
}
